import type { AssessmentScoreKey, GapStatus } from '@/services/competency.service';

export const COMPETENCY_BASE = '/grc/competency';

export const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const COMPETENCY_LEVELS = ['Basic', 'Intermediate', 'Advanced', 'Expert'];

export const REQUIREMENT_TYPES = ['Education', 'Experience', 'Skill', 'Competency', 'Certification', 'Training'] as const;

export const COURSE_CATEGORIES = ['QMS', 'HSE induction', 'Company orientation', 'On-job', 'Customer-specified', 'Change-driven', 'Other'];

export const COURSE_FREQUENCIES = ['One-time', 'Annual', 'Every N months'];

export const PLAN_SOURCES = ['TNA', 'Induction', 'NCR', 'CAPA', 'MOC', 'Customer', 'Assessment', 'Effectiveness', 'Refresher'];

export const ASSESSMENT_TYPES = ['Probationary', 'Probation re-assessment', 'Yearly', 'Role change', 'Promotion', 'Post-training', 'Other'];
export const PROBATION_TYPES = ['Probationary', 'Probation re-assessment'];

export const ASSESSMENT_PARAMETERS: Array<{ key: AssessmentScoreKey; label: string }> = [
  { key: 'score_job_knowledge', label: 'Job knowledge' },
  { key: 'score_attendance', label: 'Attendance and punctuality' },
  { key: 'score_teamwork', label: 'Team spirit and cooperation' },
  { key: 'score_communication', label: 'Relationships with peers and communication' },
  { key: 'score_productivity', label: 'Productivity' },
  { key: 'score_suggestions', label: 'Giving suggestions and feedback' },
  { key: 'score_compliance', label: 'Compliance to company policies' },
  { key: 'score_attitude', label: 'Attitude, interest for learning and improvement' },
];
export const MAX_PARAMETER_SCORE = 5;

export const GRADE_LABEL: Record<string, string> = {
  A: 'Exceptional',
  B: 'Exceed expectation',
  C: 'Meet expectation',
  D: 'Below average',
  E: 'Poor performance',
};

/** Mirrors the server: A 35-40, B 30-34, C 25-29, D 20-24, E below 20. */
export function gradeForTotal(total: number): string {
  if (total >= 35) return 'A';
  if (total >= 30) return 'B';
  if (total >= 25) return 'C';
  if (total >= 20) return 'D';
  return 'E';
}

export const GRADE_COLOR: Record<string, string> = { A: 'green', B: 'cyan', C: 'blue', D: 'orange', E: 'red' };

export function recommendationsFor(type: string): string[] {
  return PROBATION_TYPES.includes(type)
    ? ['Confirm', 'Extend probation', 'Additional training', 'Terminate']
    : ['Continue', 'Additional training', 'Terminate'];
}

export const EFFECTIVENESS_METHODS: Array<{ value: string; label: string }> = [
  { value: 'A', label: 'A - Interview' },
  { value: 'B', label: 'B - On job assessment' },
  { value: 'C', label: 'C - Observation checklist' },
  { value: 'D', label: 'D - Written test' },
];

export const EFFECTIVENESS_SCORES: Array<{ value: number; label: string }> = [
  { value: 3, label: '3 - Very good (can train others and work independently)' },
  { value: 2, label: '2 - Good (can work independently)' },
  { value: 1, label: '1 - Satisfactory (work under supervision until re-training)' },
  { value: 0, label: '0 - Poor (re-training needed)' },
];

export const GAP_COLOR: Record<GapStatus, string> = { Met: 'green', Gap: 'red', Expiring: 'gold', Expired: 'volcano' };

export const STATUS_COLOR: Record<string, string> = {
  Draft: 'default',
  Submitted: 'processing',
  Closed: 'success',
  Planned: 'blue',
  Completed: 'green',
  Cancelled: 'default',
  Open: 'gold',
  Acknowledged: 'green',
  Active: 'green',
  Expired: 'red',
  Present: 'green',
  Absent: 'red',
  Excused: 'default',
};

export const employeeLabel = (e: { employee_number?: string | null; full_name?: string | null }) =>
  [e.employee_number, e.full_name].filter(Boolean).join(' - ');
